import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { ChatMessage } from '../../../services/chatService';
import { getChatHistory, getChatSession } from '../../../services/chatService';

interface ChatPreloadState {
  isPreloading: boolean;
  isPreloaded: boolean;
  preloadedMessages: ChatMessage[];
  preloadedSessionId: string | null;
  preloadError: string | null;
  lastPreloadTime: number | null;
}

const initialState: ChatPreloadState = {
  isPreloading: false,
  isPreloaded: false,
  preloadedMessages: [],
  preloadedSessionId: null,
  preloadError: null,
  lastPreloadTime: null,
};

// Helper function to generate unique ID for messages
const generateMessageId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to process messages loaded from history
const processHistoryMessages = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.map((message) => {
    return {
      ...message,
      id: message.id || generateMessageId(),
    };
  });
};

// Async thunk to preload chat history
export const preloadChatHistory = createAsyncThunk(
  'chatPreload/preloadHistory',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const currentTime = Date.now();

    // Don't preload if already preloaded within the last 5 minutes
    const lastPreloadTime = state.chatPreload?.lastPreloadTime;
    if (lastPreloadTime && currentTime - lastPreloadTime < 5 * 60 * 1000) {
      return {
        messages: state.chatPreload.preloadedMessages,
        sessionId: state.chatPreload.preloadedSessionId,
        fromCache: true,
      };
    }

    try {
      const historyResponse = await getChatHistory(1, 1);

      if (
        historyResponse.success &&
        historyResponse.data &&
        historyResponse.data.sessions &&
        historyResponse.data.sessions.length > 0
      ) {
        const lastSession = historyResponse.data.sessions[0];

        // Skip escalated sessions - don't preload them
        if (lastSession.status === 'escalated') {
          console.log(
            'Last session was escalated, starting fresh chat instead',
          );
          return {
            messages: [],
            sessionId: null,
            fromCache: false,
          };
        }

        const sessionResponse = await getChatSession(lastSession.sessionId);

        if (
          sessionResponse.success &&
          sessionResponse.data &&
          sessionResponse.data.messages &&
          sessionResponse.data.messages.length > 0 &&
          sessionResponse.data.status !== 'escalated' // Double-check escalation status
        ) {
          const processedMessages = processHistoryMessages(
            sessionResponse.data.messages,
          );

          return {
            messages: processedMessages,
            sessionId: sessionResponse.data.sessionId,
            fromCache: false,
          };
        }
      }

      // No chat history found
      return {
        messages: [],
        sessionId: null,
        fromCache: false,
      };
    } catch (error) {
      console.log('Error preloading chat history:', error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to preload chat history',
      );
    }
  },
);

const chatPreloadSlice = createSlice({
  name: 'chatPreload',
  initialState,
  reducers: {
    clearPreloadedData: (state) => {
      return {
        ...state,
        isPreloaded: false,
        preloadedMessages: [],
        preloadedSessionId: null,
        preloadError: null,
        lastPreloadTime: null,
      };
    },
    resetPreloadError: (state) => {
      return {
        ...state,
        preloadError: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(preloadChatHistory.pending, (state) => {
        return {
          ...state,
          isPreloading: true,
          preloadError: null,
        };
      })
      .addCase(preloadChatHistory.fulfilled, (state, action) => {
        return {
          ...state,
          isPreloading: false,
          isPreloaded: true,
          preloadedMessages: action.payload.messages,
          preloadedSessionId: action.payload.sessionId,
          preloadError: null,
          // Only update timestamp if not from cache
          lastPreloadTime: !action.payload.fromCache
            ? Date.now()
            : state.lastPreloadTime,
        };
      })
      .addCase(preloadChatHistory.rejected, (state, action) => {
        return {
          ...state,
          isPreloading: false,
          isPreloaded: false,
          preloadError:
            (action.payload as string) || 'Failed to preload chat history',
        };
      });
  },
});

export const { clearPreloadedData, resetPreloadError } =
  chatPreloadSlice.actions;

export default chatPreloadSlice.reducer;

// Selectors
export const selectChatPreload = (state: { chatPreload: ChatPreloadState }) =>
  state.chatPreload;
export const selectIsPreloading = (state: { chatPreload: ChatPreloadState }) =>
  state.chatPreload.isPreloading;
export const selectIsPreloaded = (state: { chatPreload: ChatPreloadState }) =>
  state.chatPreload.isPreloaded;
export const selectPreloadedData = (state: {
  chatPreload: ChatPreloadState;
}) => ({
  messages: state.chatPreload.preloadedMessages,
  sessionId: state.chatPreload.preloadedSessionId,
});
